/**
 * Règles d'affichage de la vue intervention v2 (INT-02) — fonctions pures, aucune dépendance React.
 * Entrée : le DTO `GET /interventions/{id}/detail`. Sortie : tokens du verdict et liste ordonnée
 * de blocs à rendre. Règles : regle-gestion-vue-intervention.md §4 (R4, R9, R11, R12, R13) ;
 * couleurs : handoff README §Tokens.
 */

export const ETAT = {
    LAVE: 'LAVE',
    LAVE_MAINTENANCE: 'LAVE_MAINTENANCE',
    LAVAGE_IMPOSSIBLE: 'LAVAGE_IMPOSSIBLE',
};

export const BLOC = {
    LAVAGE: 'LAVAGE',
    LAVAGE_IMPOSSIBLE: 'LAVAGE_IMPOSSIBLE',
    LAVAGE_PARTIEL: 'LAVAGE_PARTIEL',
    CHECKLIST: 'CHECKLIST',
    BOOLEEN: 'BOOLEEN',
    MAINTENANCE_SIGNALEE: 'MAINTENANCE_SIGNALEE',
};

/** Tokens par nature de bloc (README §Tokens). */
export const NATURE_TOKENS = {
    [BLOC.LAVAGE]: {bg: '#E7ECF6', ink: '#0F2D69', dot: '#0F2D69', picto: 'Opacity'},
    [BLOC.LAVAGE_PARTIEL]: {bg: '#E7ECF6', ink: '#0F2D69', dot: '#0F2D69', picto: 'Opacity'},
    [BLOC.LAVAGE_IMPOSSIBLE]: {bg: '#FBE6E6', ink: '#B22222', dot: '#B22222', picto: 'Block'},
    [BLOC.CHECKLIST]: {bg: '#DCE8E6', ink: '#3E6B67', dot: '#538A85', picto: 'Build'},
    [BLOC.BOOLEEN]: {bg: '#FBF1D2', ink: '#9A6E00', dot: '#9A6E00', picto: 'FormatColorFill'},
    [BLOC.MAINTENANCE_SIGNALEE]: {bg: '#FDEBD2', ink: '#9A6E00', dot: '#E0A100', picto: 'Warning'},
};

export const PHOTO_LABEL_COLORS = {
    avant: '#213067',
    apres: '#88bc24',
    inaccessible: '#B22222',
    problem: '#9A6E00',
};

export const PROBLEME_TOKENS = {bg: '#FBE6E6', border: '#F0BDBD', ink: '#B22222'};

export const PAGE_BG = {default: '#F6F8F4', impossible: '#FBF4F4'};

/** Bandeau verdict : couleur de pastille, titre, dégradé, bordure, icône (README §Bandeau). */
const VERDICT_TOKENS = {
    [ETAT.LAVE]: {
        dot: '#88bc24', titleColor: '#2D333B', icon: 'Check',
        gradient: 'linear-gradient(135deg,#FBFEF6 0%,#EDF4DB 55%,#DCEAB8 100%)', border: '#CFE2A0',
    },
    [ETAT.LAVE_MAINTENANCE]: {
        dot: '#F39A3F', titleColor: '#B8641A', icon: 'Build',
        gradient: 'linear-gradient(135deg,#FFFBF6,#FDEBD8 55%,#F9D3AE)', border: '#F5C89A',
    },
    [ETAT.LAVAGE_IMPOSSIBLE]: {
        dot: '#B22222', titleColor: '#B22222', icon: 'Close',
        gradient: 'linear-gradient(135deg,#FFF8F8,#FBE6E6 55%,#F3C3C3)', border: '#EBA8A8',
    },
};

const pluralBacs = (n) => `${n} bac${n > 1 ? 's' : ''}`;

/**
 * R4 — verdict : titre, sous-ligne, tokens. Le partiel est un lavage : sous-ligne n / N réels,
 * la pilule prestation (« Lavage partiel ») porte la nuance.
 */
export function buildVerdict(detail) {
    const etat = ETAT[detail?.verdict?.etat] ?? ETAT.LAVE;
    const tokens = VERDICT_TOKENS[etat];
    const n = detail?.verdict?.nombreLaves ?? 0;
    const N = detail?.verdict?.nombreBacs ?? 0;
    const raisons = detail?.verdict?.raisons ?? [];

    const subline = etat === ETAT.LAVAGE_IMPOSSIBLE
        ? (raisons[0] ?? 'Raison non renseignée')
        : `${n} / ${pluralBacs(N)}`;

    return {
        etat,
        partiel: detail?.verdict?.partiel === true,
        title: detail?.verdict?.libelle ?? '',
        subline,
        pageBackground: etat === ETAT.LAVAGE_IMPOSSIBLE ? PAGE_BG.impossible : PAGE_BG.default,
        ...tokens,
    };
}

/** R12 — libellé de droite d'un bloc CHECKLIST. */
export function checklistSummary(elements = []) {
    const problemes = elements.filter((e) => e.statut === 'PROBLEME');
    const ok = elements.length - problemes.length;
    if (problemes.length > 0) {
        return {
            hasProbleme: true,
            label: `${problemes.length} zone${problemes.length > 1 ? 's' : ''} en problème · ${ok} OK`,
            problemes,
            oks: elements.filter((e) => e.statut !== 'PROBLEME'),
        };
    }
    return {
        hasProbleme: false,
        label: `${ok} zone${ok > 1 ? 's' : ''} OK`,
        problemes: [],
        oks: elements,
    };
}

/** R13 — libellé de droite d'un bloc BOOLEEN. */
export function booleenSummary(acte) {
    if (acte?.valeurBool === true) return {label: 'Oui · nettoyé', show: true};
    if (acte?.valeurBool === false) return {label: 'Non · aucun graffiti', show: false};
    return {label: 'Non renseigné', show: false};
}

/**
 * R9 / R11 — liste ordonnée des blocs. Lavage (ou impossible / partiel) toujours premier, puis
 * `actes[]` dans l'ordre du DTO ; compat v1 : « Maintenance signalée » si problèmes sans actes.
 * Jamais de bloc vide. Le partiel (verdict.partiel) garde ses raisons dans son bloc : elles
 * expliquent la dégradation, ce ne sont pas des problèmes de maintenance.
 */
export function buildBlocs(detail) {
    if (!detail) return [];
    const etat = detail.verdict?.etat;
    const partiel = detail.verdict?.partiel === true;
    const lavage = detail.lavage ?? {};
    const raisons = detail.verdict?.raisons ?? [];
    const actes = detail.actes ?? [];
    const N = detail.verdict?.nombreBacs ?? 0;
    const n = detail.verdict?.nombreLaves ?? 0;

    const blocs = [];

    if (partiel && etat !== ETAT.LAVAGE_IMPOSSIBLE) {
        blocs.push({
            type: BLOC.LAVAGE_PARTIEL,
            titre: 'Lavage partiel',
            valeur: `${n} / ${pluralBacs(N)}`,
            raisons,
            photosInaccessible: lavage.photosInaccessible ?? [],
            photosAvant: lavage.photosAvant ?? [],
            photosApres: lavage.photosApres ?? [],
        });
    } else if (etat === ETAT.LAVAGE_IMPOSSIBLE) {
        blocs.push({
            type: BLOC.LAVAGE_IMPOSSIBLE,
            titre: 'Lavage impossible',
            valeur: '0 bac lavé',
            raisons,
            photosInaccessible: lavage.photosInaccessible ?? [],
        });
    } else {
        blocs.push({
            type: BLOC.LAVAGE,
            titre: 'Lavage',
            valeur: `${n} / ${pluralBacs(N)}`,
            photosAvant: lavage.photosAvant ?? [],
            photosApres: lavage.photosApres ?? [],
        });
    }

    actes.forEach((acte) => {
        if (acte.nature === 'CHECKLIST') {
            const summary = checklistSummary(acte.elements ?? []);
            blocs.push({type: BLOC.CHECKLIST, titre: acte.libelle, picto: acte.picto, valeur: summary.label, acte, summary});
        } else if (acte.nature === 'BOOLEEN') {
            const summary = booleenSummary(acte);
            blocs.push({type: BLOC.BOOLEEN, titre: acte.libelle, picto: acte.picto, valeur: summary.label, acte, summary});
        }
        // LAVAGE (nature) n'existe pas en base pour l'instant : le lavage est porté par l'intervention.
    });

    // R9 compat v1 : problèmes + photosProblem sans actes, lavage accessible (jamais pour un partiel :
    // ses raisons sont déjà dans son bloc).
    if (!partiel && actes.length === 0 && etat === ETAT.LAVE_MAINTENANCE && raisons.length > 0) {
        blocs.push({
            type: BLOC.MAINTENANCE_SIGNALEE,
            titre: 'Maintenance signalée',
            valeur: `${raisons.length} problème${raisons.length > 1 ? 's' : ''}`,
            raisons,
            photosProblem: lavage.photosProblem ?? [],
        });
    }

    return blocs;
}

/**
 * Toutes les photos de l'intervention, dans l'ordre des blocs, pour la navigation de la lightbox.
 * @returns {Array<{src: string, label: string, color: string}>}
 */
export function collectPhotos(detail) {
    const photos = [];
    const push = (paths, label, color) => (paths ?? []).forEach((src) => photos.push({src, label, color}));
    buildBlocs(detail).forEach((bloc) => {
        switch (bloc.type) {
            case BLOC.LAVAGE:
                push(bloc.photosAvant, 'Avant', PHOTO_LABEL_COLORS.avant);
                push(bloc.photosApres, 'Après', PHOTO_LABEL_COLORS.apres);
                break;
            case BLOC.LAVAGE_IMPOSSIBLE:
                push(bloc.photosInaccessible, 'Lavage impossible', PHOTO_LABEL_COLORS.inaccessible);
                break;
            case BLOC.LAVAGE_PARTIEL:
                push(bloc.photosInaccessible, 'Lavage impossible', PHOTO_LABEL_COLORS.inaccessible);
                push(bloc.photosAvant, 'Avant', PHOTO_LABEL_COLORS.avant);
                push(bloc.photosApres, 'Après', PHOTO_LABEL_COLORS.apres);
                break;
            case BLOC.CHECKLIST:
                bloc.summary.problemes.forEach((e) => push(e.photos, e.libelle, PROBLEME_TOKENS.ink));
                break;
            case BLOC.BOOLEEN:
                push(bloc.acte.photos, bloc.titre, NATURE_TOKENS[BLOC.BOOLEEN].ink);
                break;
            case BLOC.MAINTENANCE_SIGNALEE:
                push(bloc.photosProblem, 'Problème', PHOTO_LABEL_COLORS.problem);
                break;
            default:
        }
    });
    return photos;
}

/** R5 (affichage) — « du JJ/MM/AAAA au JJ/MM/AAAA », bornes nulles omises. */
export function formatValidite(consigne) {
    const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString('fr-FR') : null);
    return [consigne?.dateDebut ? `du ${fmt(consigne.dateDebut)}` : null, consigne?.dateFin ? `au ${fmt(consigne.dateFin)}` : null]
        .filter(Boolean)
        .join(' ');
}

export function formatCoords(lat, lng, accuracyM) {
    if (lat == null || lng == null) return null;
    const base = `${Number(lat).toFixed(5)}, ${Number(lng).toFixed(5)}`;
    return accuracyM != null ? `${base} · ±${Math.round(accuracyM)} m` : base;
}
